import { Box, Button } from 'web-library';
import { TFunction } from 'react-i18next';
import React from 'react';
import { Theme as AugmentedTheme } from '@mui/material/styles';
import { IDialogStateHandler } from './DeleteDraftDialog';
import {
  ApplicationStatus,
  CandidateRegisteredExamListingResponse,
  ExamRegistrationType,
} from '../../../../api/candidate/exam/model';
import RouteKey from '../../../../routes/key';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import { usePostTransactionLog } from '../../../../api-refactored/candidate/usePostTransactionLog';

const { exam, amendment, withdrawal } = RouteKey.child;
const { examRegistrations, selectSubject, summary, nonNationalExaminations } = RouteKey.exam.child;
const {
  examRegistrations: examAmendment,
  editRegistration,
  viewDetails,
} = RouteKey.amendment.child;

const {
  examRegistrations: examWithdrawal,
  reason,
  summary: withdrawalSummary,
  nonNationalSummary,
} = RouteKey.withdrawal.child;

interface IMUIDataIndex {
  dataIndex: number;
  rowIndex: number;
}

interface IStyling {
  classes: any;
  theme: AugmentedTheme;
}

interface IHooks {
  history: any;
  t: TFunction<'translation'>;
}

interface IExamListingActionButtonsProps {
  data: CandidateRegisteredExamListingResponse[];
  styling: IStyling;
  hooks: IHooks;
  MUIDataIndex: IMUIDataIndex;
  dialogStateHandler: IDialogStateHandler;
}

interface IExamRoute {
  route: string;
  label: string;
  disableButton: boolean;
  txnLogType?: string;
}

interface IGetExamTypeRoutesAndLabelArgs {
  data: CandidateRegisteredExamListingResponse[];
  dataIndex: number;
  t: TFunction<'translation'>;
}

enum NonNationalExamType {
  AEIS = 'AEIS',
  S_AEIS = 'S_AEIS',
  SPERS_SEC = 'SPERS_SEC',
  SPE = 'SPE',
}

const isExamWithdrawal = (examRegistrationType: ExamRegistrationType) =>
  examRegistrationType === ExamRegistrationType.WITHDRAWAL;

const isExamAmendmentDraft = (examRegistrationType: ExamRegistrationType) =>
  examRegistrationType === ExamRegistrationType.AMENDMENT;

const isExamOrSupplementaryRegistration = (examRegistrationType: ExamRegistrationType) =>
  examRegistrationType === ExamRegistrationType.EXAM ||
  examRegistrationType === ExamRegistrationType.SUPPLEMENTARY ||
  examRegistrationType === ExamRegistrationType.LATE_SUPPLEMENTARY;

const isNonNationalRegistrationApplicationStatus = (applicationStatus: ApplicationStatus) =>
  applicationStatus === ApplicationStatus.NON_NATIONAL_PENDING ||
  applicationStatus === ApplicationStatus.NON_NATIONAL_INCOMPLETE ||
  applicationStatus === ApplicationStatus.NON_NATIONAL_INELIGIBLE;

const checkExamOrSupplementaryRoute = (examType: string, applicationUuid: string) => {
  let route: string = '';

  if (examType in NonNationalExamType) {
    switch (examType) {
      case NonNationalExamType.AEIS:
        route = `/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/${applicationUuid}/aeis/${summary.route}`;
        break;
      case NonNationalExamType.S_AEIS:
        route = `/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/${applicationUuid}/s-aeis/${summary.route}`;
        break;
      case NonNationalExamType.SPE:
        route = `/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/${applicationUuid}/spe/${summary.route}`;
        break;
      case NonNationalExamType.SPERS_SEC:
        route = `/${exam}/${examRegistrations.route}/${nonNationalExaminations.route}/${applicationUuid}/spers-sec/${summary.route}`;
        break;

      default:
        break;
    }
  } else {
    route = `/${exam}/${examRegistrations.route}/${applicationUuid}/${summary.route}`;
  }

  return route;
};

const getExamTypeRoutesAndLabel = (args: IGetExamTypeRoutesAndLabelArgs): IExamRoute => {
  const { data, dataIndex, t } = args;
  const { examRegistrationType, applicationStatus, examType, applicationUuid } = data[dataIndex];

  let route: string;
  let label: string;
  let disableButton = !data[dataIndex].timeWindowOpen;
  let txnLogType = '';

  switch (true) {
    case (isExamOrSupplementaryRegistration(examRegistrationType) &&
      applicationStatus === ApplicationStatus.REGISTERED) ||
      isNonNationalRegistrationApplicationStatus(applicationStatus):
      route = checkExamOrSupplementaryRoute(examType, applicationUuid);
      label = t('exam->>ViewRegisteredExamsListing.ViewRegistration');
      disableButton = false;
      break;
    case isExamOrSupplementaryRegistration(examRegistrationType) &&
      applicationStatus === ApplicationStatus.DRAFT:
      route = `/${exam}/${examRegistrations.route}/${data[dataIndex].applicationUuid}/${selectSubject.route}`;
      label = t('exam->>ViewRegisteredExamsListing.ContinueRegistration');
      disableButton = data[dataIndex].timeWindowOpen ? false : !data[dataIndex].supplementaryOpen;
      txnLogType = 'CONTINUE_EXAM_REGISTRATION';
      break;
    case isExamAmendmentDraft(examRegistrationType) &&
      applicationStatus === ApplicationStatus.DRAFT:
      route = `/${amendment}/${examAmendment.route}/${data[dataIndex].applicationUuid}/${editRegistration.route}`;
      label = t('exam->>ViewRegisteredExamsListing.ContinueAmendment');
      disableButton = !data[dataIndex].timeWindowOpen;
      txnLogType = 'CONTINUE_SUBJECT_AMENDMENT';
      break;
    case isExamAmendmentDraft(examRegistrationType) &&
      applicationStatus === ApplicationStatus.REGISTERED:
      route = `/${amendment}/${examAmendment.route}/${data[dataIndex].applicationUuid}/${viewDetails.route}`;
      label = t('exam->>ViewRegisteredExamsListing.ViewRegistration');
      disableButton = false;
      break;
    case isExamWithdrawal(examRegistrationType) &&
      (applicationStatus === ApplicationStatus.WITHDRAWN ||
        applicationStatus === ApplicationStatus.PENDING_APPROVAL_WITHDRAW ||
        applicationStatus === ApplicationStatus.PENDING_REVIEW_WITHDRAW ||
        applicationStatus === ApplicationStatus.WITHDRAWN_PENDING_REFUND ||
        applicationStatus === ApplicationStatus.WITHDRAWN_NO_REFUNDED ||
        applicationStatus === ApplicationStatus.WITHDRAWN_PENDING_MOE ||
        applicationStatus === ApplicationStatus.WITHDRAWN_REFUND_APPROVED ||
        applicationStatus === ApplicationStatus.WITHDRAWN_REFUNDED):
      route =
        examType in NonNationalExamType
          ? `/${withdrawal}/${examRegistrations.route}/${data[dataIndex].applicationUuid}/${nonNationalSummary.route}`
          : `/${withdrawal}/${examRegistrations.route}/${data[dataIndex].applicationUuid}/${withdrawalSummary.route}`;
      label = t('exam->>ViewRegisteredExamsListing.ViewApplication');
      disableButton = false;
      break;
    case isExamWithdrawal(examRegistrationType) && applicationStatus === ApplicationStatus.DRAFT:
      route = `/${withdrawal}/${examWithdrawal.route}/${data[dataIndex].applicationUuid}/${reason.route}`;
      label = t('exam->>ViewRegisteredExamsListing.ContinueWithdrawal');
      disableButton = !data[dataIndex].timeWindowOpen;
      txnLogType = 'CONTINUE_WITHDRAWAL_REGISTRATION';
      break;
    default:
      route = '';
      label = t('exam->>ViewRegisteredExamsListing.EmptyInstruction');
  }
  return {
    route,
    label,
    disableButton,
    txnLogType,
  };
};

const ExamListingActionButtons = (ExamListingActionProps: IExamListingActionButtonsProps) => {
  const { data, styling, hooks, MUIDataIndex, dialogStateHandler } = ExamListingActionProps;
  const { dataIndex, rowIndex } = MUIDataIndex;
  const {
    setDeleteDraftDialogOpen,
    setProceedSupplementaryModal,
    setDeleteExamType,
    setDeleteUuid,
  } = dialogStateHandler;
  const { history, t } = hooks;
  const { classes } = styling;
  const openDeleteDialogHandler = () => {
    setDeleteExamType(data[dataIndex].examType);
    setDeleteUuid(data[dataIndex].applicationUuid);
    setDeleteDraftDialogOpen(true);
  };
  const routeAndLabel = getExamTypeRoutesAndLabel({
    data,
    dataIndex,
    t,
  });

  const onActionButtonClicked = () => {
    if (
      data[dataIndex].applicationStatus === ApplicationStatus.DRAFT &&
      data[dataIndex].examRegistrationType === ExamRegistrationType.EXAM &&
      data[dataIndex].supplementaryOpen
    ) {
      setDeleteExamType(data[dataIndex].examType);
      setDeleteUuid(data[dataIndex].applicationUuid);
      setProceedSupplementaryModal(true);
    } else {
      if (routeAndLabel.txnLogType) {
        apiMutation({
          auditActionType: routeAndLabel.txnLogType,
          applicationId: data[dataIndex].applicationID,
        });
      }
      history.push(routeAndLabel.route);
    }
  };
  const { mutate: apiMutation } = usePostTransactionLog();

  return (
    <Box className={classes.actionButtons}>
      <Button
        className={classes.amendOrViewButton}
        id={`button-examRegisteredListing-row${rowIndex}`}
        variant="text"
        size="small"
        color="primary"
        onClick={onActionButtonClicked}
        disabled={routeAndLabel.disableButton}
      >
        {routeAndLabel.label}
      </Button>
      {data[dataIndex].applicationStatus === ApplicationStatus.DRAFT && (
        <>
          <DeleteButton
            buttonId={dataIndex}
            deleteHandler={openDeleteDialogHandler}
            label={t('exam->>ViewRegisteredExamsListing.Delete')}
          />
        </>
      )}
    </Box>
  );
};
export default ExamListingActionButtons;
